import type { ReactNode } from 'react';
import React, { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl-next';

import { ButtonGroup } from '@atlaskit/button';
import { Box, Text, xcss, Grid, Flex } from '@atlaskit/primitives';

// eslint-disable-next-line check-choreographer-usage/no-import-in-product-messages-without-choreographer
import Popup from '@atlaskit/popup';
import VideoIcon from '@atlaskit/icon/core/video';
import Heading from '@atlaskit/heading';
import Button, { IconButton } from '@atlaskit/button/new';
import InformationCircleIcon from '@atlaskit/icon/core/information-circle';
import type { TriggerProps } from '@atlaskit/popup';

import { messages } from '../messages';

import { ViewScriptButton } from './ViewScriptButton';
import { LoomScriptInfoPopup } from './InfoPopup';

const containerStyles = xcss({ padding: 'space.200', width: '305px' });
const contentStyles = xcss({ marginTop: 'space.200', marginBottom: 'space.100' });
const buttonGroupStyles = xcss({
	marginTop: 'space.200',
});

export type LoomScriptPopupCardProps = {
	trigger: (props: {
		isOpen: boolean;
		togglePopup: () => void;
		triggerProps: TriggerProps;
	}) => ReactNode;
	handleRecordVideo: () => void;
	isLoomButtonLoading?: boolean;
};

export const LoomScriptCallToActionPopupCard = ({
	trigger,
	handleRecordVideo,
	isLoomButtonLoading = false,
}: LoomScriptPopupCardProps) => {
	const [isOpen, setIsOpen] = useState(false);
	const togglePopup = () => setIsOpen((prev) => !prev);
	const { formatMessage } = useIntl();

	return (
		<Popup
			shouldRenderToParent
			isOpen={isOpen}
			onClose={() => setIsOpen(false)}
			placement="auto-end"
			content={() => (
				<Box xcss={containerStyles} testId="loom-script-popup-card">
					<Heading size="small">
						<FormattedMessage {...messages.popupTitle} />
					</Heading>
					<Box xcss={contentStyles}>
						<Text as="p">
							<FormattedMessage {...messages.popupPrimaryMessage} />
						</Text>
					</Box>
					<Flex alignItems="center" gap="space.025" wrap="wrap">
						<Text as="p" size="small" color="color.text.subtlest">
							<FormattedMessage {...messages.popupContextNote} />
						</Text>
						<LoomScriptInfoPopup
							trigger={({ isOpen, togglePopup, triggerProps }) => (
								<IconButton
									{...triggerProps}
									icon={InformationCircleIcon}
									label={formatMessage(messages.loonScriptValuePopupTriggerLabel)}
									appearance="subtle"
									spacing="compact"
									isSelected={isOpen}
									onClick={togglePopup}
								/>
							)}
						/>
					</Flex>
					<ButtonGroup>
						<Grid gap="space.100" autoFlow="column" xcss={buttonGroupStyles}>
							<ViewScriptButton
								onRecordVideoClick={handleRecordVideo}
								isLoading={isLoomButtonLoading}
							/>
							<Button
								appearance="primary"
								iconBefore={VideoIcon}
								onClick={handleRecordVideo}
								isLoading={isLoomButtonLoading}
							>
								<FormattedMessage {...messages.recordVideoButton} />
							</Button>
						</Grid>
					</ButtonGroup>
				</Box>
			)}
			trigger={(triggerProps) => trigger({ isOpen, togglePopup, triggerProps })}
		/>
	);
};
